import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { n8nService } from '../core/services/n8n.service';
import { queryKeys } from '../../services/query/queryKeys';
import type { CampaignMetadata } from '../../types';
import { normalizeCampaignLifecycleStatus, formatCampaignStatusLabel, isSystemManagedCampaignStatus } from '../../utils/campaignLifecycleStatus';

type EditableStatus = 'active' | 'paused' | 'archived';

export const CampaignStatusSelect: React.FC<{ campaign: CampaignMetadata; className?: string }> = ({ campaign, className = '' }) => {
  const { currentUser, targetEmail } = useAuth();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (status: EditableStatus) => {
      const email = targetEmail || currentUser?.email;
      if (!email) throw new Error('Sign in to update campaign status.');
      const response = await n8nService.updateCampaignStatus({
        campaign_id: campaign.id,
        status,
        client_email: email,
        ...(!targetEmail ? { client_id: currentUser?.uid, client_name: currentUser?.displayName || undefined } : {}),
      });
      if (!response.success) throw new Error(response.error || 'Could not save status. Try again.');
      return status;
    },
    onSuccess: (status) => {
      // The upstream campaign store syncs asynchronously. Patch confirmed writes
      // without immediately refetching an older snapshot over the new status.
      queryClient.setQueriesData<{ campaigns: CampaignMetadata[] }>({ queryKey: ['campaigns', 'all'] }, (old) =>
        old?.campaigns ? { ...old, campaigns: old.campaigns.map((item) => item.id === campaign.id ? { ...item, status } : item) } : old
      );
      queryClient.setQueryData<CampaignMetadata>(queryKeys.campaigns.detail(campaign.id), (old) => old ? { ...old, status } : old);
      window.dispatchEvent(new CustomEvent('campaign:status-changed', { detail: { campaignId: campaign.id, status } }));
    },
  });
  // Transitional/system statuses (e.g. "enriching…", "writing message…",
  // "outreaching…" — always 3-dotted) are NOT lifecycle states: they fall back
  // to Active for both the pill and the dropdown. "stopped" and "asking to
  // human" ARE display-only system statuses: shown on the pill but never
  // offered as a selectable option.
  //
  // The lifecycle bucket comes from the SHARED normalizer. The old local lookup
  // keyed only on `archived`, so a raw `archive` (and `completed`) missed every
  // branch and fell back to `'active'` — while the card's separate colour
  // normalizer DID map `archive` to grey. That split is what rendered a grey
  // pill reading "Active" and a modal reading "Archive" for one campaign.
  const rawStatus = String(campaign.status ?? '').trim().toLowerCase();
  const lifecycleStatus = normalizeCampaignLifecycleStatus(campaign.status) ?? 'active';
  // "done"/"completed" read Active as a lifecycle bucket, but the pill keeps a
  // distinct read-only label so finished work isn't mistaken for live work.
  const isDone = rawStatus === 'done' || rawStatus === 'completed';
  const isDisplayOnly = isSystemManagedCampaignStatus(campaign.status) || isDone;
  // Label always comes from the shared formatter — it owns the Done/Stopped
  // display names, so the pill and the modal can never disagree on the word.
  const readOnlyLabel = formatCampaignStatusLabel(campaign.status);

  // Display-only statuses never appear as dropdown options at all. While a
  // campaign holds one, the pill renders as a static read-only label instead
  // of a select — there is no option to "keep" it, so a select would visibly
  // snap to Active on open. Switching back to Active/Paused/Archived happens
  // by picking from the three options once the system status clears.
  if (isDisplayOnly) {
    return (
      <div className="inline-flex flex-col items-start gap-1" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
        <span
          className={`inline-flex min-h-9 max-w-full cursor-default items-center rounded-xl border px-2 py-1 text-xs font-semibold outline-none ${className}`}
          title="System status — managed by the Brainiacs engine"
        >
          {readOnlyLabel}
        </span>
        {mutation.isError && <span role="alert" className="max-w-56 text-xs text-rose-300">{mutation.error.message}</span>}
      </div>
    );
  }

  return (
    <div className="inline-flex flex-col items-start gap-1" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      <select
        aria-label={`Status for ${campaign.name}`}
        aria-busy={mutation.isPending}
        disabled={mutation.isPending}
        value={mutation.isPending ? mutation.variables : lifecycleStatus}
        onChange={(event) => mutation.mutate(event.target.value as EditableStatus)}
        className={`min-h-9 max-w-full cursor-pointer rounded-xl border px-2 py-1 text-xs font-semibold capitalize outline-none focus-visible:ring-2 focus-visible:ring-[#5260FF] disabled:cursor-wait disabled:opacity-60 ${className}`}
      >
        <option className="bg-[#101216]" value="active">Active</option>
        <option className="bg-[#101216]" value="paused">Paused</option>
        <option className="bg-[#101216]" value="archived">Archived</option>
      </select>
      {mutation.isPending && <span role="status" className="text-[10px] text-white/60">Saving…</span>}
      {mutation.isError && <span role="alert" className="max-w-56 text-xs text-rose-300">{mutation.error.message}</span>}
    </div>
  );
};
